import { useLocalStorage } from "./useLocalStorage";
import { ENABLED_LANGUAGE_SPACE_IDS, TEXT_LANGUAGES } from "../constants";
import { useEffect, useMemo } from "react";
import { useLoginStatus } from "./useLoginStatus";

export const LANGUAGE_SPACE_STORAGE_KEY = "language-space";

export type LanguageSpace = "ALL" | (typeof TEXT_LANGUAGES)[number]["id"];

export function getLanguageSpaceStorageKey(userId?: number | null) {
  if (typeof userId === "number" && Number.isFinite(userId)) {
    return `${LANGUAGE_SPACE_STORAGE_KEY}:${userId}`;
  }
  return LANGUAGE_SPACE_STORAGE_KEY;
}

export function useLanguageSpace(defaultLanguage: LanguageSpace = "ENGLISH") {
  const loginStatus = useLoginStatus();
  const userId =
    loginStatus.data?.state === "logged in" ? loginStatus.data.userId : null;
  const storageKey = getLanguageSpaceStorageKey(userId);
  const [languageSpace, setLanguageSpace] = useLocalStorage<LanguageSpace>(
    storageKey,
    defaultLanguage,
  );
  const fallbackLanguage = ENABLED_LANGUAGE_SPACE_IDS[0] ?? "ENGLISH";
  const enabledLanguageSpaceSet = useMemo(
    () => new Set<string>(ENABLED_LANGUAGE_SPACE_IDS),
    [],
  );

  useEffect(() => {
    if (languageSpace === "ALL") {
      setLanguageSpace(fallbackLanguage);
      return;
    }
    if (!enabledLanguageSpaceSet.has(languageSpace)) {
      setLanguageSpace(fallbackLanguage);
    }
  }, [enabledLanguageSpaceSet, fallbackLanguage, languageSpace, setLanguageSpace]);

  return {
    languageSpace:
      languageSpace === "ALL" || !enabledLanguageSpaceSet.has(languageSpace)
        ? fallbackLanguage
        : languageSpace,
    setLanguageSpace,
  };
}
